import * as React from 'react';
import startCase from 'lodash.startcase';
import { Folder, Inbox, MessageSquare } from 'lucide-react';
import { RiMailSendLine } from 'react-icons/ri';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useHub } from '@/contexts/hub-context';
import { useNylasFolders } from '@/hooks/use-nylas-folders';
import { useGetUserSetting, useSetUserSetting } from '@/hooks/use-settings';
import { AccountSwitcher } from '../components/account-switcher';
import { CompactContactsPanel } from '../components/compact-contacts-panel';

function getAccounts(verifyGrantId: string | undefined, myGrantId: string | undefined, myGrantEmail: string | undefined) {
  const accounts = [];

  if (verifyGrantId) {
    accounts.push({
      label: 'verify@ftld.com',
      email: 'verify@ftld.com',
      grantId: verifyGrantId,
      icon: <RiMailSendLine className="size-4" />,
    });
  }

  if (myGrantId && myGrantEmail) {
    accounts.push({
      label: myGrantEmail,
      email: myGrantEmail,
      grantId: myGrantId,
      icon: <RiMailSendLine className="size-4" />,
    });
  }

  return accounts;
}

interface LeftPaneProps {
  isCollapsed: boolean;
}

export function LeftPane({ isCollapsed }: LeftPaneProps) {
  const {
    selectedInbox,
    setSelectedInbox,
    selectedFolderId,
    setSelectedFolderId,
    myGrantId,
    myGrantEmail,
    verifyGrantId,
    sendAsGrantId,
    setSendAsGrantId,
    setSendAsEmailAddress,
  } = useHub();

  const { data: myFolders } = useNylasFolders(myGrantId, { queryParams: { limit: 100 } });
  const { data: verifyFolders } = useNylasFolders(verifyGrantId, { queryParams: { limit: 100 } });

  // Fetch the default account setting
  const { data: commDefaultSetting, isLoading: isCommDefaultLoading } = useGetUserSetting(
    'communications_default_to_verify',
    { enabled: !!verifyGrantId || !!myGrantId }, // Only fetch if at least one grant ID is available
  );

  // Get the mutation function to set the setting
  const setSetting = useSetUserSetting();

  // Ref to track if the initial setting has been applied
  const initialSettingApplied = React.useRef(false);

  // Set initial account based on the setting, only once
  React.useEffect(() => {
    // Exit if already applied, or if data is still loading
    if (initialSettingApplied.current || isCommDefaultLoading) {
      return;
    }

    // Only proceed if we have grant IDs available
    if (verifyGrantId || myGrantId) {
      let settingValue = commDefaultSetting?.value; // Get current setting value or undefined

      // Handle the case where the setting is not found AFTER loading is complete
      if (!commDefaultSetting) {
        console.warn(
          'User setting "communications_default_to_verify" not found. Applying default \'false\' and creating setting.',
        );
        // Trigger mutation to create the setting with default value
        setSetting.mutate({ key: 'communications_default_to_verify', value: 'false' });
        // Set the effective value to 'false' for the current execution
        settingValue = 'false';
      }

      // Determine the default based on the effective setting value
      const defaultToVerify = settingValue === 'true';
      let applied = false; // Track if we successfully applied a setting

      if (defaultToVerify && verifyGrantId) {
        setSendAsGrantId(verifyGrantId);
        setSendAsEmailAddress('verify@ftld.com');
        applied = true;
      } else if (!defaultToVerify && myGrantId && myGrantEmail) {
        setSendAsGrantId(myGrantId);
        setSendAsEmailAddress(myGrantEmail);
        applied = true;
      } else if (verifyGrantId) { // Fallback to verify if available
        setSendAsGrantId(verifyGrantId);
        setSendAsEmailAddress('verify@ftld.com');
        applied = true;
      } else if (myGrantId && myGrantEmail) { // Fallback to user's email if available
        setSendAsGrantId(myGrantId);
        setSendAsEmailAddress(myGrantEmail);
        applied = true;
      }

      if (applied) {
        initialSettingApplied.current = true; // Mark as applied
      }
    }
    // Dependencies: Ensure all used variables/functions are listed.
  }, [
    isCommDefaultLoading,
    commDefaultSetting, // State used for check and value
    verifyGrantId, // Used in logic
    myGrantId, // Used in logic
    myGrantEmail, // Used in logic
    setSendAsGrantId, // Called in logic
    setSendAsEmailAddress, // Called in logic
    setSetting, // Called conditionally
  ]);

  return (
    <ResizablePanelGroup direction="vertical" className="h-full">
      <ResizablePanel defaultSize={60} minSize={30}>
        <div className={isCollapsed ? 'h-[52px]' : 'p-2'}>
          <AccountSwitcher
            isCollapsed={isCollapsed}
            accounts={getAccounts(verifyGrantId, myGrantId, myGrantEmail)}
            value={sendAsGrantId}
            onChange={({ email, grantId }) => {
              setSendAsGrantId(grantId);
              setSendAsEmailAddress(email);
            }}
          />
        </div>
        <Separator />
        <ScrollArea className="h-full">
          <div className="space-y-2 p-2">
            <button
              type="button"
              onClick={() => {
                setSelectedFolderId(undefined);
                setSelectedInbox('deal');
              }}
              className={`flex w-full items-center gap-2 rounded-lg px-4 py-2 hover:bg-accent ${selectedInbox === 'deal' ? 'bg-accent font-semibold' : ''
              }`}
            >
              <MessageSquare className="size-4" />
              <span className={`${isCollapsed ? 'hidden' : ''}`}>Deal Communications</span>
            </button>
            <Separator />
            <Accordion type="single" collapsible className="space-y-1">
              {verifyGrantId && (
                <AccordionItem value="verify" className="border-0">
                  <AccordionTrigger
                    onClick={() => {
                      setSelectedFolderId(undefined);
                      setSelectedInbox('verify@ftld.com');
                    }}
                    className={`rounded-lg px-4 py-2 hover:bg-accent hover:no-underline ${selectedInbox === 'verify@ftld.com' ? 'bg-accent font-semibold' : ''
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Inbox className="size-4" />
                      <span className={`${isCollapsed ? 'hidden' : ''}`}>verify@ftld.com</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-0 pt-1">
                    <div className="space-y-1 pl-6">
                      {verifyFolders?.map((folder) => (
                        <button
                          key={folder.id}
                          type="button"
                          onClick={() => {
                            setSelectedFolderId(folder.id);
                            setSelectedInbox('verify@ftld.com');
                          }}
                          className={`flex w-full items-center gap-2 rounded-lg px-4 py-2 text-sm hover:bg-accent ${selectedFolderId === folder.id ? 'bg-accent font-semibold' : ''}`}
                        >
                          <Folder className="size-4" />
                          <span className={`${isCollapsed ? 'hidden' : ''}`}>{startCase(folder.name.toLowerCase())}</span>
                        </button>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              )}

              {myGrantId && myGrantEmail && myFolders && (
                <AccordionItem value="grant" className="border-0">
                  <AccordionTrigger
                    onClick={() => {
                      setSelectedFolderId(undefined);
                      setSelectedInbox(myGrantEmail);
                    }}
                    className={`rounded-lg px-4 py-2 hover:bg-accent hover:no-underline ${selectedInbox === myGrantEmail ? 'bg-accent font-semibold' : ''
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Inbox className="size-4" />
                      <span className={`${isCollapsed ? 'hidden' : ''}`}>{myGrantEmail}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-0 pt-1">
                    <div className="space-y-1 pl-6">
                      {myFolders?.map((folder) => (
                        <button
                          key={folder.id}
                          type="button"
                          onClick={() => {
                            setSelectedFolderId(folder.id);
                            setSelectedInbox(myGrantEmail);
                          }}
                          className={`flex w-full items-center gap-2 rounded-lg px-4 py-2 text-sm hover:bg-accent ${selectedFolderId === folder.id ? 'bg-accent font-semibold' : ''}`}
                        >
                          <Folder className="size-4" />
                          <span className={`${isCollapsed ? 'hidden' : ''}`}>{startCase(folder.name.toLowerCase())}</span>
                        </button>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              )}
            </Accordion>
          </div>
        </ScrollArea>
      </ResizablePanel>
      <ResizableHandle withHandle />
      <ResizablePanel defaultSize={40} minSize={30}>
        <CompactContactsPanel />
      </ResizablePanel>
    </ResizablePanelGroup>
  );
}
